import { type FC, useMemo, useState } from "react";

import type { SourceControlRepository } from "../../types";

import { ChevronLeftIcon, ChevronRightIcon } from "../common/icons";
import { RepoActionPopover } from "../common/RepoActionPopover";

type IndexingLevel = "indexed" | "stale" | "never";

const COLS = 3;
const ROWS = 5;
const PAGE_SIZE = COLS * ROWS;

function getIndexingLevel(repo: SourceControlRepository): IndexingLevel {
  if (repo.index_freshness) {
    if (repo.index_freshness.state === "OK") {
      return "indexed";
    }
    if (repo.index_freshness.state === "STALE") {
      return "stale";
    }
    if (repo.index_freshness.state === "NONE") {
      return "never";
    }
    return "never";
  }

  if (!repo.last_indexed_at) {
    return "never";
  }
  const ageMs = Date.now() - repo.last_indexed_at * 1000;
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return ageMs > sevenDaysMs ? "stale" : "indexed";
}

const LEVEL_CLASS: Record<IndexingLevel, string> = {
  indexed:
    "bg-emerald-500/20 border-emerald-500/30 dark:bg-emerald-400/15 dark:border-emerald-400/25",
  never:
    "bg-slate-200/50 border-slate-300/50 dark:bg-slate-700/30 dark:border-slate-600/40",
  stale:
    "bg-amber-500/20 border-amber-500/30 dark:bg-amber-400/15 dark:border-amber-400/25",
};

const LEVEL_DOT: Record<IndexingLevel, string> = {
  indexed: "bg-emerald-500 dark:bg-emerald-400",
  never: "bg-slate-400 dark:bg-slate-500",
  stale: "bg-amber-500 dark:bg-amber-400",
};

const LEVEL_LABEL: Record<IndexingLevel, string> = {
  indexed: "Indexed",
  never: "Not indexed",
  stale: "Stale",
};

function formatIndexedAt(repo: SourceControlRepository): string {
  if (!repo.last_indexed_at) {
    return "Never indexed";
  }
  const date = new Date(repo.last_indexed_at * 1000);
  return `Indexed ${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}

function shortSlug(slug: string): string {
  return slug.includes("/") ? slug.split("/").pop()! : slug;
}

const RepoPopoverInfo: FC<{
  repo: SourceControlRepository;
  level: IndexingLevel;
}> = ({ repo, level }) => (
  <div className="space-y-1">
    <div>{repo.remote_slug}</div>
    <div className="flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 ${LEVEL_DOT[level]}`} />
      {LEVEL_LABEL[level]}
    </div>
    <div className="text-slate-400 dark:text-slate-500">
      {formatIndexedAt(repo)}
    </div>
    {repo.language && (
      <div className="text-slate-400 dark:text-slate-500">{repo.language}</div>
    )}
  </div>
);

interface SidebarRepoGridProps {
  repositories: SourceControlRepository[];
  onNavigateToIndexing: (repoSlug: string) => void;
}

export const SidebarRepoGrid: FC<SidebarRepoGridProps> = ({
  repositories,
  onNavigateToIndexing,
}) => {
  const [page, setPage] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(repositories.length / PAGE_SIZE)),
    [repositories.length],
  );

  const pageRepos = useMemo(
    () => repositories.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [repositories, page],
  );

  if (repositories.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          Repositories
        </span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-0.5">
            <span className="h-1.5 w-1.5 bg-emerald-500 dark:bg-emerald-400" />
            <span className="font-mono text-[8px] text-slate-400 dark:text-slate-500">
              OK
            </span>
          </span>
          <span className="flex items-center gap-0.5">
            <span className="h-1.5 w-1.5 bg-amber-500 dark:bg-amber-400" />
            <span className="font-mono text-[8px] text-slate-400 dark:text-slate-500">
              STALE
            </span>
          </span>
          <span className="flex items-center gap-0.5">
            <span className="h-1.5 w-1.5 bg-slate-400 dark:bg-slate-500" />
            <span className="font-mono text-[8px] text-slate-400 dark:text-slate-500">
              NONE
            </span>
          </span>
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {pageRepos.map((repo) => {
          const level = getIndexingLevel(repo);
          return (
            <RepoActionPopover
              key={repo.repository_id}
              side="right"
              className="flex min-w-0"
              info={<RepoPopoverInfo repo={repo} level={level} />}
              actions={[
                {
                  label: "Index",
                  onClick: () => onNavigateToIndexing(repo.remote_slug),
                },
              ]}
            >
              <div
                className={`flex w-full items-center gap-1 border px-1 py-px ${LEVEL_CLASS[level]}`}
              >
                <span className={`h-1 w-1 flex-shrink-0 ${LEVEL_DOT[level]}`} />
                <span className="truncate font-mono text-[8px] text-slate-600 dark:text-slate-400">
                  {shortSlug(repo.remote_slug)}
                </span>
              </div>
            </RepoActionPopover>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-30 dark:text-slate-500 dark:hover:text-slate-300"
          >
            <ChevronLeftIcon size={12} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i)}
              className={`h-1 w-1 transition-colors ${
                i === page
                  ? "bg-slate-600 dark:bg-slate-300"
                  : "bg-slate-300 hover:bg-slate-400 dark:bg-slate-600 dark:hover:bg-slate-500"
              }`}
            />
          ))}
          <button
            type="button"
            disabled={page === totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-30 dark:text-slate-500 dark:hover:text-slate-300"
          >
            <ChevronRightIcon size={12} />
          </button>
        </div>
      )}
    </div>
  );
};
